/**
 * Derived credit-rate table for "how credits work" surfaces (Compare Plans
 * dialog, cost estimator modal).
 *
 * Derivation rule: private audiences bill at the Premium tier (`tier2`), so
 * every row below reads its rate off the tier2 slot of the shared-types
 * constants re-exported by `@/lib/billing/credit-rates`. No numeric literal
 * is ever inlined for a rate: this module exists specifically to kill the
 * class of stale-copy bug where a dialog's hardcoded rate drifts from the
 * real pricing constant after a re-price (the "0.4 vs 0.3" bug).
 */

import {
  AUDIENCE_CREATION_CREDITS,
  CREDITS_PER_RESPONDENT,
  QUICK_STUDY_CREDITS_PER_READ,
  CHAT_CREDITS_PER_MSG,
  ROOM_CREDITS_PER_MSG,
} from '@/lib/billing/credit-rates'
import {
  ASSISTANT_IMAGE_CREDITS,
  CONVERSATION_LEVEL_CREDIT_MULTIPLIER,
  SOCIAL_CREDITS_PER_150_POSTS,
  SOCIAL_POSTS_PER_CREDIT_BLOCK,
  assistantLevelRate,
  assistantLevelRateExact,
  roomMessageCredits,
  typicalCredits,
  type ConversationLevel,
} from '@digital-twins/shared-types'

export interface CreditRateRow {
  action: string
  rate: number
  unit: string
  /** One row that carries several prices (image quality levels), shown side by side. */
  variants?: Array<{ rate: number }>
}

/**
 * Today's table. Read only for an account that is not enrolled in credit metering: an enrolled
 * account's Chat and Focus group prices come from `roomRateRows()` (`roomMessageCredits`).
 */
export const PRIVATE_CREDIT_RATES: CreditRateRow[] = [
  { action: 'Audience creation', rate: AUDIENCE_CREATION_CREDITS, unit: 'per twin' },
  { action: 'Study', rate: CREDITS_PER_RESPONDENT.tier2, unit: 'per respondent' },
  { action: 'Quick study', rate: QUICK_STUDY_CREDITS_PER_READ.tier2, unit: 'per respondent per question' },
  { action: 'Chat', rate: CHAT_CREDITS_PER_MSG.tier2, unit: 'per message' },
  { action: 'Focus group', rate: ROOM_CREDITS_PER_MSG.tier2, unit: 'per message' },
]

/**
 * Social listening's rate row: credits per block of posts added, read off the
 * shared-types constants. Separate from PRIVATE_CREDIT_RATES (audience-tier
 * rates) so the scenario estimator does not list it.
 */
export const SOCIAL_LISTENING_CREDIT_RATE: CreditRateRow = {
  action: 'Social listening',
  rate: SOCIAL_CREDITS_PER_150_POSTS,
  unit: `per ${SOCIAL_POSTS_PER_CREDIT_BLOCK} posts added`,
}

export const MARKETPLACE_RATE_FOOTNOTE = '* Marketplace and demo audiences may vary in rates.'

/** Credit metering (enrolled accounts only): the Assistant levels, in table order. */
export const ASSISTANT_PRICE_LEVELS: ReadonlyArray<ConversationLevel> = ['low', 'medium', 'high']

export interface AssistantRateRow {
  level: ConversationLevel
  inputCreditsPerMillion: number
  cachedInputCreditsPerMillion: number
  outputCreditsPerMillion: number
  /** Typical credits per message from the shared price list. */
  typicalCreditsPerMessage: number
  /** The same three rates unrounded: dollar figures price from these, never from the rounded display. */
  exact: {
    inputCreditsPerMillion: number
    cachedInputCreditsPerMillion: number
    outputCreditsPerMillion: number
  }
}

/** Per-million rates and the typical cost per message of each level, from the price owner. */
export function assistantRateRows(): Array<AssistantRateRow> {
  return ASSISTANT_PRICE_LEVELS.map((level) => ({
    level,
    ...assistantLevelRate(level),
    exact: assistantLevelRateExact(level),
    typicalCreditsPerMessage: typicalCredits(level),
  }))
}

export interface RoomRateRow {
  kind: 'chat' | 'focus_group'
  /** Credits per message at each level, Premium audience, enrolled list. */
  byLevel: Record<ConversationLevel, number>
}

/** Room message prices per level (Premium audience) from `roomMessageCredits`, no new video. */
export function roomRateRows(): Array<RoomRateRow> {
  return (['chat', 'focus_group'] as const).map((kind) => ({
    kind,
    byLevel: Object.fromEntries(
      ASSISTANT_PRICE_LEVELS.map((level) => [
        level,
        roomMessageCredits({ kind, tier: 'tier2', level, carriesNewVideo: false, enrolled: true }),
      ]),
    ) as Record<ConversationLevel, number>,
  }))
}

/** The level multipliers, exposed so a table never retypes them. */
export const ROOM_LEVEL_MULTIPLIERS = CONVERSATION_LEVEL_CREDIT_MULTIPLIER

/**
 * The flat rows of "how credits work". Not enrolled: today's rows. Enrolled: the Chat and Focus
 * group rows are replaced by level rows (low / medium / high) and image rows join (study, Quick Study and images are
 * unchanged by metering).
 */
export function creditRateRows(enrolled: boolean): Array<CreditRateRow> {
  if (!enrolled) {
    return PRIVATE_CREDIT_RATES
  }
  const imageRow: CreditRateRow = {
    action: 'Image',
    rate: ASSISTANT_IMAGE_CREDITS.medium,
    unit: 'per image',
    variants: (['medium', 'high'] as const).map((level) => ({ rate: ASSISTANT_IMAGE_CREDITS[level] })),
  }
  const assistantRows = assistantRateRows()
  /* The weighted rate is shown whole (10 / 15 / 30): the price owner's 14.99 and 30.03 are the
     same prices carried through a conversion, so the display rounds them. */
  const assistantWeightedRow: CreditRateRow = {
    action: 'Assistant weighted',
    rate: Math.round(assistantRows[1].inputCreditsPerMillion),
    unit: 'per million weighted tokens',
    variants: assistantRows.map((row) => ({ rate: Math.round(row.inputCreditsPerMillion) })),
  }
  const assistantTypicalRow: CreditRateRow = {
    action: 'Assistant typical',
    rate: assistantRows[1].typicalCreditsPerMessage,
    unit: 'per message',
    variants: assistantRows.map((row) => ({ rate: row.typicalCreditsPerMessage })),
  }
  const roomRows = roomRateRows().map((row): CreditRateRow => ({
    action: row.kind === 'chat' ? 'Chat levels' : 'Focus group levels',
    rate: row.byLevel.medium,
    unit: 'per message',
    variants: ASSISTANT_PRICE_LEVELS.map((level) => ({ rate: row.byLevel[level] })),
  }))
  return [
    ...PRIVATE_CREDIT_RATES.filter((row) => row.action !== 'Chat' && row.action !== 'Focus group'),
    imageRow,
    assistantWeightedRow,
    assistantTypicalRow,
    ...roomRows,
  ]
}
